import React, { useContext, useState, useMemo, useLayoutEffect, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { cancelTripApi, getLastTrip } from "_services";
import CustomPagination from "components/CustomPagination";
import CustomTooltip from "components/CustomTooltip/CustomTooltip";
import { Images } from "constants";
import { UserContext } from "context";
import { utils } from "helper";
import { ConfirmModalFor, EndTripModal, LrDetailModal } from "modals";
import Loading from "pages/Loading";
import toast from "react-hot-toast";
import {
  IoChevronBack,
  IoChevronDownCircleOutline,
  IoChevronForward,
  IoChevronUp,
  IoChevronDown,
} from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import StorageService from "utils/StorageServices";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaRegTrashCan } from "react-icons/fa6";
import { TbArrowsSort } from "react-icons/tb"; // Add this import for unsorted state
import { IoIosArrowRoundDown, IoIosArrowRoundUp } from "react-icons/io";
import { Skeleton } from "components/common";
import { TableRowSkeleton } from "components/common/skeleton/Skeleton";
import { useQueryClient } from "@tanstack/react-query";
import './tripDispatchTab.css'

// Custom Filter Component with dynamic placeholders
const Filter = ({ column, table }) => {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  // Define placeholders for each column
  const getPlaceholder = (columnId) => {
    const placeholders = {
      trip_number: "Search trip...",
      dispatch_date: "Search date...",
      party: "Search party...",
      from_station: "Search from...",
      to_station: "Search to...",
      end_date: "Search end date...",
      branch_name: "Search branch...",
      vehicle_number: "Search vehicle...",
      driver_supplier: "Search driver/supplier...",
      status: "Search status...",
    };
    return placeholders[columnId] || "Search...";
  };

  return typeof firstValue === "number" ? (
    <div className="flex space-x-2">
      <input
        type="number"
        value={(columnFilterValue)?.[0] ?? ""}
        onChange={(e) =>
          column.setFilterValue((old) => [e.target.value, old?.[1]])
        }
        placeholder="Min"
        className="w-24 border shadow rounded"
        style={{
          fontSize: "12px",
          padding: "4px 6px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          width: "auto"
        }}
      />
      <input
        type="number"
        value={(columnFilterValue)?.[1] ?? ""}
        onChange={(e) =>
          column.setFilterValue((old) => [old?.[0], e.target.value])
        }
        placeholder="Max"
        className="w-24 border shadow rounded"
        style={{
          fontSize: "12px",
          padding: "4px 6px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          width: "auto"
        }}
      />
    </div>
  ) : (
    <input
      type="text"
      value={(columnFilterValue ?? "")}
      onChange={(e) => column.setFilterValue(e.target.value)}
      placeholder={getPlaceholder(column.id)}
      className="w-36 border shadow rounded"
      style={{
        fontSize: "12px",
        padding: "4px 6px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        width: "120px"
      }}
    />
  );
};

// Sorting Icon Component
const SortingIcon = ({ column }) => {
  const sortDirection = column.getIsSorted();

  if (sortDirection === 'asc') {
    return <IoIosArrowRoundUp style={{ marginLeft: '4px', fontSize: '14px' }} />;
  } else if (sortDirection === 'desc') {
    return <IoIosArrowRoundDown style={{ marginLeft: '4px', fontSize: '14px' }} />;
  } else {
    return <TbArrowsSort style={{ marginLeft: '4px', fontSize: '14px', opacity: 0.5 }} />;
  }
};

const AllDispatchDetailTab = ({
  list,
  loading,
  handlePageChange,
  filter,
  totalItems,
  itemsPerPage,
  callBackLrId,
  callTripList = () => { }
}) => {
  const { masterData, allMenu } = useContext(UserContext);
  const navigate = useNavigate();
  const [showLrDetail, setShowLrDetail] = useState(false);
  const [showAddLR, setShowAddLR] = useState(false);
  const childsUser = allMenu?.find((i) => i?.label === "Trip");
  const [cancleTripOpen, setCancleTripOpen] = useState(false);
  const [showEndTrip, setShowEndTrip] = useState(false);
  const [lastTripDetails, setLastTripDetails] = useState();
  const [lastLoading, setLastLoading] = useState({});
  const [cancleLoading, setCancleLoading] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const queryClient = useQueryClient();
  const rowRefs = useRef({});
  const prevPositions = useRef({});




  const handleClick = (event) => {
    event.stopPropagation();
  };

  const handleDetailsAction = (e, data) => {
    e.stopPropagation();
    setShowLrDetail(data);
  };

  const handleNextModal = (data) => {
    setShowLrDetail(!showLrDetail);
    setShowAddLR(data);
  };

  const planFromLoc = (loc) => {
    return loc.map((item) => item.from_city).join(" + ");
  };

  const planToLoc = (loc) => {
    return loc.map((item) => item.to_city).join(" + ");
  };

  const partyJoin = (trip) => {
    const names = trip.lr_list
      ?.map((i) => i?.consignor_id?.name)
      .filter(Boolean);
    const uniqueNames = [...new Set(names)];
    return uniqueNames.join(" | ");
  };

  const getLastTripdetails = async (data) => {
    setLastLoading(true);
    const vehicleId = data?._id;

    setLastLoading((prev) => ({ ...prev, [vehicleId]: true }));
    const res = await getLastTrip({
      company_id: StorageService.getCompanyId(),
      vehicle_id: data?._id,
    });
    if (res?.responseCode === 200) {
      setLastLoading((prev) => ({ ...prev, [vehicleId]: false }));
      setLastTripDetails(res.data?.trip);
      setShowEndTrip(res.data?.trip);
    } else {
      setLastLoading((prev) => ({ ...prev, [vehicleId]: false }));
      toast.error(res?.responseMessage);
    }
  };

  const cancleTripHandle = async (data) => {
    setCancleLoading(true);
    const vehicleId = data?._id;

    const res = await cancelTripApi({
      company_id: StorageService.getCompanyId(),
      trip_id: cancleTripOpen?._id,
    });
    if (res?.responseCode === 200) {
      setCancleLoading(false);
      callBackEndForList();
      setCancleTripOpen(false);
      toast.success(res?.responseMessage);
    } else {
      setCancleLoading(false);
      toast.error(res?.responseMessage);
    }
  };

  const callBackEndForList = () => {
    callTripList();
    queryClient.invalidateQueries([callTripList]);
  }

  // TanStack Table Column Definitions with sorting and filtering
  const columns = useMemo(
    () => [
      {
        id: "actions",
        header: "",
        size: 30, // Made smaller
        minSize: 30,
        maxSize: 30,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const trip = row.original;
          return (
            <div className="_separateBxs">
              <div className="signDropdown">
                <button className="dropbtn11">
                  <BsThreeDotsVertical />
                </button>
                {masterData?.trip_status[trip?.trip_status] !== "Cancelled" &&
                  masterData?.trip_status[trip?.trip_status] !== "Delivered" && (
                    <div className="dropdownContent">
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setCancleTripOpen(trip);
                        }}
                        className="_avPointer"
                      >
                        <span className="_samAnchor">
                          <span className="deleteIcon_avt">
                            <FaRegTrashCan color="red" />
                          </span>
                          Cancel
                        </span>
                      </div>
                    </div>
                  )}

              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "trip_number",
        header: "Trip Number",
        sortingFn: "alphanumeric",
        enableColumnFilter: true,
        filterFn: "includesString",
        cell: ({ row }) => {
          const trip = row.original;
          return (
            <div className="_separateBxs">
              <Link
                className="_trip_ind me-2"
                to={`/trip/addtrip/dispatched/${trip?._id}`}
                onClick={handleClick}
              >
                <span>Trip</span>
                <div className="trpCode">
                  {trip?.trip_number ? trip?.trip_number : ""}
                </div>
              </Link>
            </div>
          );
        },
      },
      {
        accessorKey: "dispatch_date",
        header: "Start Date",
        sortingFn: "datetime",
        sortUndefined: 1,
        enableColumnFilter: true,
        filterFn: "includesString",
        cell: ({ row }) => {
          const trip = row.original;
          return (
            <div className="_dateNtime _pDate">
              <p className="_tblText_inDent">
                {trip?.dispatch_date
                  ? utils.formatDateMonth(trip?.dispatch_date)
                  : ""}
              </p>
              <p className="_lgtText_inDent">
                {trip?.dispatch_date
                  ? utils.formatTime(trip?.dispatch_date)
                  : ""}
              </p>
            </div>
          );
        },
      },
      {
        id: "party",
        header: "Party",
        accessorFn: (row) => partyJoin(row),
        sortingFn: "alphanumeric",
        enableColumnFilter: true,
        filterFn: "includesString",
        cell: ({ row }) => {
          const trip = row.original;
          const value = String(partyJoin(trip) || "");
          return value.length > 20 ? (
            <CustomTooltip txtClass="__tooltipTxt_bx" cstStyle={{ maxWidth: "138px" }}>
              <p>{value}</p>
            </CustomTooltip>
          ) : (
            <div className="__NottooltipTxt">
              <p>{value || "-"}</p>
            </div>
          );
        },
      },
      {
        id: "Firm",
        header: "Firm",
        accessorFn: (row) => row?.firm_id?.firm || "", // Return empty string for proper filtering
        sortingFn: "alphanumeric",
        enableColumnFilter: true,
        filterFn: "includesString",
        cell: ({ row }) => {
          const firmName = row.original?.firm_id?.firm || "-";

          return firmName !== "-" && firmName.length > 20 ? (
            <CustomTooltip txtClass="__tooltipTxt_bx" cstStyle={{ maxWidth: "138px" }}>
              <p>{firmName}</p>
            </CustomTooltip>
          ) : (
            <div className="__NottooltipTxt">
              <p>{firmName}</p>
            </div>
          );
        },
      },
      {
        id: "from_station",
        header: "From Station",
        accessorFn: (row) => planFromLoc(row?.from_location),
        sortingFn: "alphanumeric",
        enableColumnFilter: true,
        filterFn: "includesString",
        cell: ({ row }) => {
          const trip = row.original;
          const value = String(planFromLoc(trip?.from_location) || "");
          return value.length > 24 ? (
            <CustomTooltip txtClass="__tooltipTxt_bx">
              <p>{value}</p>
            </CustomTooltip>
          ) : (
            <div className="__NottooltipTxt">
              <p>{value || "-"}</p>
            </div>
          );
        },
      },
      {
        id: "to_station",
        header: "To Station",
        accessorFn: (row) => planToLoc(row?.to_location),
        sortingFn: "alphanumeric",
        enableColumnFilter: true,
        filterFn: "includesString",
        cell: ({ row }) => {
          const trip = row.original;
          const value = String(planToLoc(trip?.to_location) || "");
          return value.length > 24 ? (
            <CustomTooltip txtClass="__tooltipTxt_bx">
              <p>{value}</p>
            </CustomTooltip>
          ) : (
            <div className="__NottooltipTxt">
              <p>{value || "-"}</p>
            </div>
          );
        },
      },
      {
        accessorKey: "end_date",
        header: "End Date",
        sortingFn: "datetime",
        sortUndefined: 1,
        enableColumnFilter: true,
        filterFn: "includesString",
        cell: ({ row }) => {
          const trip = row.original;
          return (
            <div className="_dateNtime _pDate">
              {trip?.end_date ? (
                <>
                  <p className="_tblText_inDent">
                    {trip?.end_date
                      ? utils.formatDateMonth(trip?.end_date)
                      : "-"}
                  </p>
                  <p className="_lgtText_inDent">
                    {trip?.end_date ? utils.formatTime(trip?.end_date) : ""}
                  </p>
                </>
              ) : (
                <button
                  className="end_trip_btn_list"
                  onClick={(e) => {
                    e.stopPropagation();
                    getLastTripdetails(trip?.vehicle_id);
                  }}
                  disabled={
                    lastLoading[trip?.vehicle_id?._id] || trip?.trip_status == 5
                  }
                >
                  {lastLoading[trip?.vehicle_id?._id]
                    ? "Processing..."
                    : "End Trip"}
                </button>
              )}
            </div>
          );
        },
      },
      {
        id: "branch_name",
        header: "Branch Name",
        accessorFn: (row) => row?.branch_id?.branch || "",
        sortingFn: "alphanumeric",
        enableColumnFilter: true,
        filterFn: "includesString",
        cell: ({ row }) => {
          const trip = row.original;
          return trip?.branch_id?.branch ? trip?.branch_id?.branch : "-";
        },
      },
      {
        id: "vehicle_number",
        header: "Vehicle Number",
        accessorFn: (row) => row?.vehicle_id?.vehicle_no || "",
        sortingFn: "alphanumeric",
        enableColumnFilter: true,
        filterFn: "includesString",
        cell: ({ row }) => {
          const trip = row.original;
          const vehicleNo = trip?.vehicle_id?.vehicle_no;
          // Format vehicle number only if it exists
          const formattedVehicleNo = vehicleNo
            ? vehicleNo.length === 9
              ? `${vehicleNo.slice(0, 4)} ${vehicleNo.slice(4, 5)} ${vehicleNo.slice(5)}`
              : `${vehicleNo.slice(0, 4)} ${vehicleNo.slice(4, 6)} ${vehicleNo.slice(6)}`
            : "N/A"; // or "" for empty string

          return (
            <>
              <div className="_trckDetail">
                <p>{formattedVehicleNo}</p>
              </div>
              <div className="__vrhDetailBx">
                <div
                  className={
                    trip?.vehicle_ownership == 1
                      ? "_trkMark _lrDelivered_status"
                      : trip?.vehicle_ownership == 2
                        ? "_trkMark _lrPartyPoint_status"
                        : "_trkMark _lrInTransit_status"
                  }
                >
                  {masterData?.vehicle_ownership[trip?.vehicle_ownership]}
                </div>
                <div className="__containrType">
                  {trip?.lorry_type_id?.lorry_type}
                </div>
              </div>
            </>
          );
        },
      },
      {
        id: "driver_supplier",
        header: "Driver/ Supplier",
        // accessorFn: (row) => {
        //   return row?.vehicle_id?.vehicle_ownership == 1
        //     ? `Driver - ${row?.driver_id?.name ? row?.driver_id?.name : ""}`
        //     : `Supplier - ${row?.supplier_id?.name ? row?.supplier_id?.name : ""}`;
        // },
        // sortingFn: "alphanumeric",
        // enableColumnFilter: true,
        // filterFn: "includesString",
        // cell: ({ row }) => {
        //   const trip = row.original;
        //   return trip?.vehicle_id?.vehicle_ownership == 1
        //     ? `Driver - ${trip?.driver_id?.name ? trip?.driver_id?.name : ""}`
        //     : `Supplier - ${trip?.supplier_id?.name ? trip?.supplier_id?.name : ""} ${trip?.driver_id?.name ? `| Driver - ${trip?.driver_id?.name}` : ""}`;
        // },

        accessorFn: (row) => {
          const driverName = row?.driver_id?.name ?? "";
          const supplierName = row?.supplier_id?.name ?? "";
          const ownership = row?.vehicle_id?.vehicle_ownership;

          return ownership === 1 ? `${supplierName}|${driverName}` : `${supplierName}|${driverName}`;
        },


        cell: ({ row }) => {
         
          let driver = row?.original?.driver_id?.name ?? "";
          let supplier = row?.original?.supplier_id?.name ?? "";

          return (
            <div className="flex flex-col gap-1.5">
              {supplier && <div className="__supDrv_Bx">
                <span>{supplier}</span>
                <div className="__btmHead">Supplier</div>
              </div>}

              {driver && <div className="__supDrv_Bx">
                <span>{driver}</span>
                <div className="__btmHead">Driver</div>
              </div>}
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (row) => masterData?.trip_status[row?.trip_status] || "",
        sortingFn: "alphanumeric",
        enableColumnFilter: true,
        filterFn: "includesString",
        cell: ({ row }) => {
          const trip = row.original;
          return (
            <span
              className={
                masterData?.trip_status[trip?.trip_status] === "Pending"
                  ? "_lrPending_status"
                  : masterData?.trip_status[trip?.trip_status] === "In Transit"
                    ? "_lrInTransit_status"
                    : masterData?.trip_status[trip?.trip_status] === "At Party Point"
                      ? "_lrPartyPoint_status"
                      : masterData?.trip_status[trip?.trip_status] === "Delivered"
                        ? "_lrDelivered_status"
                        : masterData?.trip_status[trip?.trip_status] === "Bill Raised"
                          ? "_lrDelivered_status"
                          : masterData?.trip_status[trip?.trip_status] === "Cancelled"
                            ? "_lrCanceled_status"
                            : ""
              }
            >
              {masterData?.trip_status[trip?.trip_status]}
            </span>
          );
        },
      },
      {
        id: "view",
        header: "View",
        size: 50, // Made smaller
        minSize: 50,
        maxSize: 50,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const trip = row.original;
          return (
            <div className="">
              <button
                className="accordion-button _plnChev_btn collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={`#${trip?._id}`}
                aria-expanded="false"
                aria-controls={`${trip?._id}`}
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <IoChevronDownCircleOutline />
              </button>
            </div>
          );
        },
      },
    ],
    [masterData, lastLoading, cancleTripOpen]
  );

  // Prepare data for TanStack Table
  const data = useMemo(() => list?.trip_list || [], [list?.trip_list]);

  // Create table instance with sorting and filtering
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    globalFilterFn: "includesString",
  });

  useLayoutEffect(() => {
    const newPositions = {};

    // Record new positions
    table.getRowModel().rows.forEach(row => {
      const domNode = rowRefs.current[row.id];
      if (domNode) {
        const rect = domNode.getBoundingClientRect();
        newPositions[row.id] = rect;
      }
    });

    // Animate from old position to new
    table.getRowModel().rows.forEach(row => {
      const domNode = rowRefs.current[row.id];
      const prev = prevPositions.current[row.id];
      const current = newPositions[row.id];

      if (domNode && prev) {
        const deltaY = prev.top - current.top;

        if (deltaY !== 0) {
          domNode.style.transform = `translateY(${deltaY}px)`;
          domNode.style.transition = 'transform 0s';

          requestAnimationFrame(() => {
            domNode.style.transform = '';
            domNode.style.transition = 'transform 600ms ease';
          });
        }
      }
    });

    // Update previous positions
    prevPositions.current = newPositions;
  }, [table.getRowModel().rows]);
  // Render expandable row content
  const renderSubComponent = ({ row }) => {
    const trip = row.original;
    return (
      <div
        id={`${trip?._id}`}
        className="accordion-collapse collapse"
        data-bs-parent="#indentTableAccordian"
      >
        <div className="accordion-body">
          {trip?.lr_list?.map((lr) => (
            <div key={lr?._id} className="__tMax_bx">
              <div className="_poDetails">
                <span>LR Date</span>
                <p>
                  {lr?.dispatch_date
                    ? utils.formatDateMonth(lr.dispatch_date)
                    : ""}
                </p>
              </div>

              <div className="_poDetails">
                <span>LR Number</span>
                <p>{lr?.lr_number || "-"}</p>
              </div>

              <div className="_poDetails">
                <span>Invoice Number</span>
                <p>{lr?.invoice_number || "-"}</p>
              </div>

              <div className="_poDetails">
                <span>Party</span>
                <p>{lr?.consignor_id?.name || "-"}</p>
              </div>

              <div className="_poDetails">
                <span>From Unit</span>
                {lr?.unit_id ? (
                  <>
                    <p>
                      {lr.unit_id.code && lr.unit_id.name
                        ? `${lr.unit_id.code} | ${lr.unit_id.name}`
                        : lr.unit_id.code || lr.unit_id.name}
                    </p>
                    <p style={{ fontSize: "12px" }}>{lr.unit_id.address}</p>
                  </>
                ) : (
                  <p>-</p>
                )}
              </div>

              <div className="_poDetails">
                <span>To Consignee</span>
                {lr?.consignee_id ? (
                  <>
                    <p>
                      {lr.consignee_id.code && lr.consignee_id.name
                        ? `${lr.consignee_id.code} | ${lr.consignee_id.name}`
                        : lr.consignee_id.code || lr.consignee_id.name}
                    </p>
                    <p style={{ fontSize: "12px" }}>
                      {lr.consignee_id.address}
                    </p>
                  </>
                ) : (
                  <p>-</p>
                )}
              </div>

              <div className="_poDetails">
                <span>Eway Valid Till</span>
                <p>
                  {lr?.eway_expiry_date
                    ? utils.formatDateMonth(lr.eway_expiry_date)
                    : "-"}
                </p>
              </div>

              <div className="_poDetails">
                <span>Eway Bill Number</span>
                <p>{lr?.eway_bill_number || "-"}</p>
                <p style={{ fontSize: "12px" }}>
                  {lr?.eway_bill_date
                    ? utils.formatDateMonth(lr.eway_bill_date)
                    : "-"}
                </p>
              </div>

              <div className="__plStatus">
                <div className="_poDetails">
                  <span>LR Status</span>
                </div>
                <span
                  className={
                    {
                      Pending: "_lrPending_status",
                      "In Transit": "_lrInTransit_status",
                      "At Party Point": "_lrPartyPoint_status",
                      Delivered: "_lrDelivered_status",
                      "Bill Raised": "_lrBillRaised_status",
                    }[masterData?.lr_status[lr?.lr_status]] || ""
                  }
                >
                  {masterData?.lr_status[lr?.lr_status] || ""}
                </span>
              </div>

              {/* <div className="_poDetails"></div> */}
            </div>
          ))}
        </div>
      </div>
    );
  };

  let endTripModal = {
    isOpen: showEndTrip,
    closeModal: (e) => {
      setShowEndTrip(false);
    },
    modalMainClass: "_dashCommon_Modal _smModal",
    modalTitle: "End Trip",
    submitTitle: "End Trip",
    callBackEndForList: callBackEndForList,
    lastTripDetails: lastTripDetails,
  };

  let cancleTripProps = {
    show: cancleTripOpen,
    closeModal: (e) => setCancleTripOpen(false),
    title: "Cancel Trip",
    submitTitle: "Cancel Trip",
    onSubmit: cancleTripHandle,
    isLoadding: cancleLoading,
    disabled: cancleLoading,
  };


  return (
    <>
      <div className="_indentTable _planLTable _disPatchView __trpDetail __supTripBxs">
        <div className="accordion" id="indentTableAccordian">
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  <tr>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          width: header.id === "actions" ? "10px" :
                            header.id === "view" ? "50px" : undefined
                        }}
                        onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                        className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          {header.column.getCanSort() && (
                            <SortingIcon column={header.column} />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>

                </React.Fragment>
              ))}
            </thead>

            {data.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tbody
                  key={row.id}
                  className="accordion-item"

                  onClick={() =>
                    childsUser?.view == 1
                      ? navigate(`/trip/addtrip/dispatched/${row.original._id}`)

                      : ""
                  }
                >
                  <tr className="_tableRowAnimate" key={row.id}
                    ref={el => (rowRefs.current[row.id] = el)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}  >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td colSpan={13} className="p-0 border-0">
                      {renderSubComponent({ row })}
                    </td>
                  </tr>
                </tbody>
              ))
            ) :
              loading ? (

                <tbody className="accordion-item">
                  <TableRowSkeleton
                    columns={table.getAllColumns().length}
                    rows={25}
                    animation="luxury" // Try: shimmer, glow, luxury, wave, premium
                  />
                </tbody>
              ) :
                (
                  <tbody className="accordion-item">
                    <tr>
                      <td colSpan="100%" style={{ textAlign: "center" }}>
                        No data available
                      </td>
                    </tr>
                  </tbody>
                )}

          </table>
        </div>
      </div>

      {list?.trip_list?.length > 0 && (
        <div className="paginationFt_avt ">
          <CustomPagination
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            currentPage={filter?.offset}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      <div
        className={`offcanvas offcanvas-end common_offCanvas xxlOffcanvas ${showLrDetail ? "show" : ""
          }`}
        tabIndex={-1}
        style={{ visibility: showLrDetail ? "visible" : "hidden" }}
      >
        {showLrDetail && (
          <LrDetailModal
            isOpen={showLrDetail}
            onClose={(e) => setShowLrDetail(false)}
            nextModal={(e) => handleNextModal(e)}
          />
        )}
      </div>
      <div className={showLrDetail ? "offBack_drop" : ""}></div>

      {showEndTrip && <EndTripModal {...endTripModal} />}
      {cancleTripOpen && (
        <ConfirmModalFor {...cancleTripProps}>
          <>
            <p className="_confirmPrx">
              Do you really want Cancel this Trip ?
            </p>
          </>
        </ConfirmModalFor>
      )}
    </>
  );
};

export default AllDispatchDetailTab;